import { Descriptions, Form, message, Space, UploadFile } from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import { FormItem } from 'components/Form/FormItem';
import { ImageUpload } from 'components/ImageUpload/ImageUpload';
import { FORM_LAYOUT_CONFIG } from 'consts/common';
import { useMeterContext } from 'contexts/Meter';
import { VFC } from 'react';
import { SerializedInventoryMeter } from '@enmon/commons/lib/tools';
import { RegisterState } from 'routes/meter_replacement/useStore';
import { useServices } from 'services/useServices';
import { useNavigate, useParams } from 'react-router-dom';
import { captureException } from '@sentry/react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSessionStore } from 'store/session';
import { Registers } from './Registers';
import { SERVICE_INSPECTION_FORM_ID } from '../constants';
import { getInitialServiceInspectionFormState } from '../utils/getInitialFormState';
import {
  getServiceInspectionPayloadData,
  getServiceInspectionPayloadPhotos,
} from '../utils/getServiceInspectionPayload';
import { useServiceInspectionStore } from '../useStore';
import { ServiceInspection as messages } from '../messages';

export interface ServiceInspectionFormValues {
  placementOverviewPhotos?: UploadFile[];
  internalNote?: SerializedInventoryMeter['internal_note'];
  registerStates?: RegisterState[];
}

export const ServiceInspectionContent: VFC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { email } = useSessionStore();
  const { enmonMeter } = useMeterContext();
  const intl = useIntl();

  const inventoryMeterId = parseInt(id ?? '');
  const [form] = Form.useForm<ServiceInspectionFormValues>();
  const {
    api: { inventoryMetersApiClient },
  } = useServices();

  const { setIsSending } = useServiceInspectionStore();

  const initialFormValues = getInitialServiceInspectionFormState(enmonMeter);
  const selectedRegisterStates = Form.useWatch(['registerStates'], { form });
  const selectedPlacementOverviewPhotos = Form.useWatch(['placementOverviewPhotos'], { form });

  const handleSubmit = async (values: ServiceInspectionFormValues) => {
    const payloadData = { ...getServiceInspectionPayloadData(values), inspected_by: email };
    const payloadPhotos = getServiceInspectionPayloadPhotos(values);

    setIsSending(true);
    await inventoryMetersApiClient
      .serviceInspection({ data: payloadData, formPhotos: payloadPhotos, inventoryMeterId })
      .then(() => {
        message.success(intl.formatMessage(messages.recordSentSuccess));
        navigate(`/`);
      })
      .catch(error => {
        message.error(intl.formatMessage(messages.recordSentError));
        console.error(error);
        captureException(error);
      })
      .finally(() => {
        setIsSending(false);
      });
  };

  const handleRegisterPhotoSelect = (fieldName: number, fileList: UploadFile[]) => {
    const updatedRegisterStates = selectedRegisterStates?.map((registerState, index) =>
      index === fieldName ? { ...registerState, photos: fileList } : registerState,
    );
    form.setFieldsValue({ registerStates: updatedRegisterStates });
  };

  return (
    <Space className="w-100" direction="vertical" size="large">
      <Descriptions
        bordered
        column={{ xxl: 2, xl: 2, lg: 2, md: 2, sm: 1, xs: 1 }}
        size="small"
        title={<FormattedMessage {...messages.title} />}
      >
        <Descriptions.Item label={<FormattedMessage {...messages.strapiId} />}>{enmonMeter?.toolsId}</Descriptions.Item>
        <Descriptions.Item label={<FormattedMessage {...messages.sn} />}>{enmonMeter?.serial}</Descriptions.Item>
      </Descriptions>
      <Form
        form={form}
        id={SERVICE_INSPECTION_FORM_ID}
        initialValues={initialFormValues}
        layout="horizontal"
        onFinish={handleSubmit}
        {...FORM_LAYOUT_CONFIG}
      >
        <FormItem
          required
          label={<FormattedMessage {...messages.placementOverviewPhotos} />}
          name={'placementOverviewPhotos' satisfies keyof ServiceInspectionFormValues}
          rules={[
            {
              required: true,
              message: intl.formatMessage(messages.placementOverviewPhotosRequired),
            },
          ]}
        >
          <ImageUpload
            multiple
            fileList={selectedPlacementOverviewPhotos}
            name="placementOverviewPhotos"
            onImageUpload={fileList => form.setFieldsValue({ placementOverviewPhotos: fileList })}
          />
        </FormItem>
        <FormItem label={<FormattedMessage {...messages.note} />} name="internalNote">
          <TextArea autoSize={{ minRows: 3, maxRows: 5 }} placeholder={intl.formatMessage(messages.notePlaceholder)} />
        </FormItem>
        <Registers onPhotoSelect={handleRegisterPhotoSelect} />
      </Form>
    </Space>
  );
};
